import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'react-feather';

import { Badge, Loader } from '../../../ui';
import type { BadgeTone } from '../../../ui';
import {
  AiSummary,
  CHANNEL_LABELS,
  SENTIMENT_STYLE,
  StudentSentiment,
  fetchAiSummaries,
  summaryMonth,
} from '../../../Tasks/tasksApi';
import { Segmented } from './ui-pending';
import './AiSummaryCard.scss';

/**
 * A student's monthly AI summary — what their support history across
 * Freshdesk, chat, calls, PTM notes and class ratings said, so an RM can
 * open a conversation already knowing the month's story.
 *
 * Used in the ticket panel and detail page (behind a button, so nothing is
 * fetched until an agent asks) and on the student profile, where the whole
 * month history is worth browsing.
 */

/** Sentiment → badge tone (labels still come from SENTIMENT_STYLE). */
const SENTIMENT_TONE: Record<StudentSentiment, BadgeTone> = {
  POSITIVE: 'success',
  NEUTRAL: 'neutral',
  MIXED: 'warning',
  NEGATIVE: 'danger',
};

type ListTone = 'default' | 'danger' | 'success';

const List = ({ items, tone }: { items: string[]; tone: ListTone }): JSX.Element | null => {
  if (!items?.length) return null;
  return (
    <ul className={`ais-list ais-list--${tone}`}>
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
};

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element => (
  <div className="ais-section">
    <div className="ais-section__title">{title}</div>
    {children}
  </div>
);

export const AiSummaryBody = ({ summary }: { summary: AiSummary }): JSX.Element => {
  const [showChannels, setShowChannels] = useState(false);
  const sentiment = SENTIMENT_STYLE[summary.sentiment] ? summary.sentiment : 'NEUTRAL';
  const tone = SENTIMENT_STYLE[sentiment];
  // Channels that actually had something to say this month.
  const channels = Object.entries(summary.channelInsights ?? {}).filter(
    ([, insight]) => insight?.summary || (insight?.count ?? 0) > 0,
  );

  /**
   * A month with nothing behind it: the pipeline still writes a row, but
   * "Neutral" there means "we saw nothing", not a read on the student. Say
   * that in one line instead of dressing it up as an assessment.
   */
  const quietMonth =
    !channels.length && !summary.agentFocusAreas?.length && !summary.openConcerns?.length;
  if (quietMonth) {
    return (
      <div className="ais-quiet">
        No support interactions recorded in {summaryMonth(summary.period)} — nothing for the AI to
        summarise.
      </div>
    );
  }

  return (
    <div className="ais-body">
      <div className="ais-head">
        <Badge tone={SENTIMENT_TONE[sentiment] ?? 'neutral'}>{tone.label}</Badge>
        <span className="rm-caption">{summaryMonth(summary.period)}</span>
      </div>

      <p className="ais-summary">{summary.summary}</p>

      {!!summary.agentFocusAreas?.length && (
        <Section title="What to focus on">
          <List items={summary.agentFocusAreas} tone="default" />
        </Section>
      )}
      {!!summary.openConcerns?.length && (
        <Section title="Open concerns">
          <List items={summary.openConcerns} tone="danger" />
        </Section>
      )}
      {!!summary.positiveHighlights?.length && (
        <Section title="Going well">
          <List items={summary.positiveHighlights} tone="success" />
        </Section>
      )}

      {!!channels.length && (
        <div className="ais-channels">
          <button
            type="button"
            className="ais-link"
            aria-expanded={showChannels}
            onClick={() => setShowChannels((v) => !v)}
          >
            {showChannels ? <ChevronUp size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}
            Where this came from ({channels.length})
          </button>
          {showChannels && (
            <div className="ais-channels__list">
              {channels.map(([key, insight]) => (
                <div key={key} className="ais-channel">
                  <span className="ds-strong">{CHANNEL_LABELS[key] ?? key}</span>
                  {typeof insight.count === 'number' && (
                    <span className="ds-text-muted"> · {insight.count}</span>
                  )}
                  {insight.summary && <div className="ds-text-muted">{insight.summary}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="rm-caption">
        Generated {new Date(summary.generatedAt).toLocaleDateString('en-IN')} · {summary.model}
      </div>
    </div>
  );
};

interface Props {
  studentExternalId: string;
  /** 'latest' shows only the newest month; 'history' adds a month switcher. */
  variant?: 'latest' | 'history';
  /** Wrap in a bordered box (the ticket panel) or render bare (profile tab). */
  boxed?: boolean;
}

const AiSummaryCard = ({ studentExternalId, variant = 'latest', boxed = true }: Props): JSX.Element => {
  const [rows, setRows] = useState<AiSummary[] | null>(null);
  const [error, setError] = useState('');
  const [index, setIndex] = useState(0);

  useEffect(() => {
    let live = true;
    setRows(null);
    setError('');
    setIndex(0);
    fetchAiSummaries(studentExternalId)
      .then((data) => live && setRows(data))
      .catch((e) => live && setError(e.message || 'Could not load the summary'));
    return () => {
      live = false;
    };
  }, [studentExternalId]);

  const inner = ((): JSX.Element => {
    if (error) return <div className="ais-error">{error}</div>;
    if (!rows) return <Loader label="Loading summary…" />;
    if (!rows.length) {
      return (
        <div className="ais-quiet">
          No AI summary for this student yet. They&apos;re generated monthly from support
          history — a student with no interactions won&apos;t have one.
        </div>
      );
    }
    const current = rows[Math.min(index, rows.length - 1)];
    return (
      <>
        {variant === 'history' && rows.length > 1 && (
          <Segmented
            size="sm"
            className="ais-months"
            ariaLabel="Summary month"
            value={String(index)}
            onChange={(v) => setIndex(Number(v))}
            options={rows.map((row, i) => ({ value: String(i), label: summaryMonth(row.period) }))}
          />
        )}
        <AiSummaryBody summary={current} />
      </>
    );
  })();

  if (!boxed) return inner;
  return <div className="ais-box">{inner}</div>;
};

export default AiSummaryCard;
